/*
 * Copyright (c) 2017 - 2020 CalAmp Corp.  All Rights Reserved
 */
import axios, {AxiosResponse} from 'axios';
import isNil from 'lodash.isnil';
import qs from 'qs';
import {ApplicationContext} from '@ion/core/src/context/ApplicationContext';
import {error, info} from '../logger/logger';
import {GenericObject} from '../types/entities';
import {contains} from '../utils/StringUtil';

export enum Environment {
  local = 'local',
  dev = 'dev',
  qa = 'qa',
  stg = 'stg',
  us = 'us',
  eu = 'eu',
};

/**
 * Defines the environment object which holds all configuration values
 * provided by the environment.json document. This object is to be
 * considered private and only assigned a value via the load and /
 * or __inject__ functions, respectively.
 */
let _config: GenericObject;

/**
 * The get method provides a single source of access to environment.json
 * configuration values. Calls to get ensure the underlying environment
 * configuration which has been loaded remain immutable.
 *
 * For a list of all available keys, see environment.json.
 *
 * ```typescript
 *   // get the appKey entry from environment.json ...
 *   const appKey = get('appKey'); // app key value ...
 *
 *   // get the passwordMinLength and passwordMaxLength entries ...
 *   const policy = get('passwordMinLength', 'passwordMaxLength');
 *   console.log(policy.passwordMinLength) // 'passwordMinLength' value ...
 *   console.log(policy.passwordMaxLength) // 'passwordMaxLength' value ...
 *
 * ```
 *
 * @see ./src/static/environment.json
 *
 * @method  get
 * @param   {string|array} entry Defines the environment.json configuration
 *          entry, or an array of entries, which are to be returned.
 * @returns {string|number|boolean} The value of the given environment.json
 *          configuration entry or entries.
 */
export const get = (...entry) => {
  if (_config && entry) {
    if (entry.length > 1) {
      const entries = {};
      entry.forEach(key => {
        entries[key] = _config[key];
      });
      return entries;
    }
    return _config[entry[0]];
  }
};

//TODO - Temporary provide ability to switch between OIDC & non OIDC auth flows
const enableOidc = (config, enable) => {
  config.oidcEnabled = enable;
  info(`OIDC is${enable ? '' : 'not'} Enabled`);
};

/**
 * Provides a convenience function for determining if the application is explicitly
 * running in a local development environment such as localhost or a local IPV4 or
 * IPV6 address. If the host argument is falsy, particularly `undefined` or an
 * empty `string`, the host will default to `globalThis?.location?.hostname`.
 *
 * ```typescript
 *     isDevelopmentEnvironment('http://localhost:4000'); // true
 *     isDevelopmentEnvironment('http://127.0.0.1'); // true
 *     isDevelopmentEnvironment('somedomain.local'); // true
 *     isDevelopmentEnvironment('https://somedomain.com'); // false
 * ```
 */
export const isDevelopmentEnvironment = (host?: string, includeLoopback = true): boolean => {
  const domain  = host || globalThis?.location?.hostname;
  const isLocal = (
    contains(domain, 'localhost') ||
    contains(domain, '192.168') ||
    contains(domain, '.local')
  );
  return includeLoopback ? (isLocal || isCommonLoopbackIP(host)) : isLocal;
};

/**
 * Provides a utility which determines if the application is being ran from a
 * common localhost loopback IP address (127.0.0.1).
 *
 * ```typescript
 *     isCommonLoopbackIP('http://localhost:4000'); // false
 *     isCommonLoopbackIP('http://127.0.0.1'); // true
 * ```
 *
 * @returns `true` if the application is being run from a localhost loopback
 *          address (127.0.0.1), otherwise, `false`.
 */
export const isCommonLoopbackIP = (host?: string): boolean => {
  const domain  = host || globalThis?.location?.hostname;
  return contains(domain, '127.0.0.1');
};

/**
 * Provides a convenience function for determining if the application is running
 * in a local development environment.
 */
export const isLocal = (host?: string): boolean => {
  return getDeployedEnvironment(host) === Environment.local;
};

/**
 * Provides a convenience function for determining if the application is running
 * in a pre-prod dev environment.
 */
export const isDev = (host?: string): boolean => {
  return getDeployedEnvironment(host) === Environment.dev;
};

/**
 * Provides a convenience function for determining if the application is running
 * in a pre-prod qa environment.
 */
export const isQA = (host?: string): boolean => {
  return getDeployedEnvironment(host) === Environment.qa;
};

/**
 * Provides a convenience function for determining if the application is running
 * in a pre-prod staging environment.
 */
export const isStaging = (host?: string): boolean => {
  return getDeployedEnvironment(host) === Environment.stg;
};

/**
 * Provides a convenience function for determining if the application is running
 * in a production environment (us, eu).
 */
export const isProduction = (host?: string): boolean => {
  return isUSProduction(host) || isEUProduction(host);
};

/**
 * Provides a convenience function for determining if the application is running
 * in the US Production environment.
 */
export const isUSProduction = (host?: string): boolean => {
  return getDeployedEnvironment(host) === Environment.us;
};

/**
 * Provides a convenience function for determining if the application is running
 * in the EU Production environment.
 */
export const isEUProduction = (host?: string): boolean => {
  return getDeployedEnvironment(host) === Environment.eu;
};

/**
 * Provides a convenience function for determining if the application is running
 * in a pre-prod environment (dev, dev1, dev2, dev3, qa, qa1, qa2, staging, stg1
 * stg2).
 */
export const isPreProd = (host?: string): boolean => {
  return !isLocal(host) && !isProduction(host);
};

/**
 * Provides a convenience function which returns the environment in which the
 * application is executing.
 *
 * ```typescript
 *
 *   import {getEnvironment, Environment} from '@ion/core/src/environment/environment';
 *
 *   const env: Environment = getEnvironment();
 *
 * ```
 */
export const getEnvironment = (host?: string): Environment => {
  return getDeployedEnvironment(host);
};

/**
 * @private
 *
 * Determines the environment to which the application has been deployed.
 */
const getDeployedEnvironment = (src?: string): Environment => {
  const host =  src || globalThis.location.host;
  if (contains(host, 'calamp')) {
    const match = Object.keys(Environment).find(env => contains(host, env));
    const env   = !match && contains(host, 'staging') ? Environment.stg : match;
    if (!env) {
      return Environment[!contains(host, 'co.uk') ? 'us' : 'eu'];
    }
    return Environment[env];
  }
  return Environment.local;
};

/**
 * Provides a convenience function which returns the context of iOn deployment
 * i.e. iOn Suite or reseller (assistcargo, etc).
 *
 * ```typescript
 *
 *   import {getApplicationContext} from '@ion/core/src/environment/environment';
 *
 *   const context: ApplicationContext = getApplicationContext();
 *
 * ```
 */
export const getApplicationContext = (src?: string): ApplicationContext => {
  const host =  src || globalThis.location.host;
  const keys = Object.keys(ApplicationContext);
  for (const key of keys) {
    if (contains(host, key)) {
      return ApplicationContext[key];
    }
  }
  return ApplicationContext.ion;
};

/**
 * Provides an async alias to loadConfig.
 */
export const getConfig = async() => await loadConfig();

/**
 * The loadConfig method loads the external environment.json configuration
 * of the deployed application. This method is to be invoked prior to the
 * initial render call of the application.
 *
 * The environment.json document defines static keys referenced by the
 * CalAmp iOn UI for configuring external environment configurations, such
 * as the application key, service root url, etc.
 *
 * Keys are defined within environment.json at design time by engineering,
 * and the values for which are managed by Product Operations based on the
 * deployment context / environment.
 *
 * For a list of all available environment keys (and their default values),
 * see /src/static/environment.json. Likewise, additional keys (and their
 * default values) can be defined in environment.json.
 *
 * ```typescript
 *
 *   // load the environment.json configuration, then render!
 *     loadConfig(() => {
 *       render();
 *   });
 *
 * ```
 *
 * @method loadConfig
 * @param  {function} callback to be invoked upon loading of environment.json.
 * @param  {bool} noCache When true (default), indicates that the environment.json
 *         document is to be loaded via a simple cache busting strategy whereby a
 *         `ts` (timestamp) parameter is appended to the query string with the value
 *         of the current time.
 * @param  {object} source Allows for providing a source configuration to be used as
 *          an alternative to async retrieval.
 */
export const loadConfig = async(cb?: Function, noCache = true, source?: object) => {
  if (!_config) {
    if (!source) {
      info('Loading Environment Configuration ...');
      const params   = qs.parse(location.search, {ignoreQueryPrefix: true});
      const document = params?.env ? `environment-${params.env}` : 'environment';
      const url      = `/${document}.json${noCache ? `?ts=${new Date().getTime()}` : ''}`;
      try {
        const response: AxiosResponse = await axios.get(url, {headers: {'content-type': 'application/json'}});
        _config = response?.data;
        info('Environment Configuration Successfully Loaded.');
        //TODO - Temporary provide ability to switch between OIDC & non OIDC auth flows
        overrideConfigValue(_config, params);
        cb?.(_config);
        return _config;
      } catch (err) {
        error(`Environment Configuration error, could not retrieve ${url}.`);
        cb?.(null);
      }
    } else {
      info('Environment Configuration initialized from source.');
      _config = source;
      cb(_config);
    }
  }
};

/**
 * Returns a cloned representation of the underlying environment.json
 * configuration.
 *
 * @method clone
 */
export const clone = (): object => {
  return Object.assign({}, _config);
};

/**
 * @private
 *
 * Provides a pseudo-private hook for setting the environment configuration
 * object internally.
 */
export const __inject__ = (config: any, force = false) => {
  if (!_config || force) {
    _config = config;
  }
};

export {get as default};

/**
 * @private
 *
 * Provides a convenience function to override environment.json entries without rebuilding app or
 * updating puppet config. Presently you can override only boolean entries, although we could extend it
 * to update any value. This can be done only for pre-prod environments.
 * Lets assume that environment.json has these entries
 * "featureFlagsEnabled" : false,
 * "oidcEnabled" : false,
 * To override them we launch ion as
 * https://qa.ion.calamp.com/?oidcEnabled=true&featureFlagsEnabled=true
 *
 */
const overrideConfigValue = (config, params) => {
  for (const prop of Object.keys(params)) {
    const {[prop]: value} = config;
    if (!isNil(value)) {
      config[`_${prop}`] = config[prop];
      config[prop] = params[prop]?.toLowerCase() === 'true' || false;
      info(`${prop} is ${params[prop]}`);
    }
  }
};

/**
 * @private
 *
 * Provides a mechanism to run unit tests on non-exported functions.
 * NOT to be imported in production code.
 */
export const exportedForTesting = {
  overrideConfigValue
};
